import React, { useState } from 'react';
import { Box, Typography, Paper, Tabs, Tab } from '@mui/material';
import {
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  LibraryBooks as LibraryBooksIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { useUserOrganizations } from '../hooks/useOrganization';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';
import CSBBulkImport from '../components/CSBBulkImport';

const SUPERADMIN_EMAILS = [
  'alex.samaniego@ppiweb.com',
  'tim.autrey@ppiweb.com',
];

/**
 * Documents page — upload and manage organization documents for analysis.
 * Superadmins see an additional tab for CSB Bulk Import.
 */
export default function Documents() {
  const { user } = useAuth();
  const { organizations, loading: orgLoading } = useUserOrganizations(user?.uid);
  const organization = organizations.length > 0 ? organizations[0] : null;
  const orgId = organization?.id || null;

  const isSuperAdmin = user && SUPERADMIN_EMAILS.includes(user.email);
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <DescriptionIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">
          Document Upload Portal
        </Typography>
      </Box>

      {/* Tabs: Upload | CSB Bulk Import (superadmin only) */}
      {isSuperAdmin ? (
        <>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 3 }}
          >
            <Tab
              icon={<CloudUploadIcon />}
              iconPosition="start"
              label="Upload"
            />
            <Tab
              icon={<LibraryBooksIcon />}
              iconPosition="start"
              label="CSB Bulk Import"
            />
          </Tabs>

          {tab === 0 && (
            <UploadTab orgId={orgId} orgLoading={orgLoading} user={user} />
          )}
          {tab === 1 && (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Batch import CSB investigation reports for AI analysis. Reports are processed via the
                analysis pipeline (Path B — REPSit.ai-managed) and published as global case studies.
              </Typography>
              <CSBBulkImport />
            </Box>
          )}
        </>
      ) : (
        <UploadTab orgId={orgId} orgLoading={orgLoading} user={user} />
      )}
    </Box>
  );
}

/** Standard upload tab content */
function UploadTab({ orgId, orgLoading, user }) {
  return (
    <>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        REPSit.ai analyzes and learns from all documents uploaded to its Learning Library. For
        each item uploaded, it identifies and extracts drift and accumulation patterns that
        existed prior to the associated incident/event, including their impacts on each of the
        REPS Quadrants.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <strong>What belongs here:</strong> Industry incident and event investigation reports,
        including agency reports (e.g., CSB reports, NERC reports, etc.), internal reports such as
        causal analyses, close-call/near-miss write-ups, and field observation notes.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Supported formats for document upload: PDF, DOCX, TXT, CSV (max 25 MB).
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <strong>NOTE:</strong> By default, internal documents are NOT added to the shared REPSit.ai
        database and remain secure within your system. Should you upload documents that you think
        will benefit others outside of your organization, you may toggle the{' '}
        <strong>Add to REPSit Common Database</strong> to ON.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        <strong>NOT for this page:</strong> Org charts and HR rosters (these belong in{' '}
        <strong>Settings → Organization</strong>) and day-to-day observations, which are entered in the{' '}
        <strong>OE Condition Entry Portal</strong>.
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Upload Document
        </Typography>
        {orgLoading ? (
          <Typography color="text.secondary">Loading organization...</Typography>
        ) : !orgId ? (
          <Typography color="text.secondary">
            No organization found. Please join or create an organization first.
          </Typography>
        ) : (
          <DocumentUpload
            orgId={orgId}
            userId={user?.uid}
            onUploadComplete={(result) => {
              console.log('Upload complete:', result);
            }}
          />
        )}
      </Paper>

      {/* Document list with real-time updates */}
      {orgId && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Uploaded Documents
          </Typography>
          <DocumentList orgId={orgId} />
        </Paper>
      )}
    </>
  );
}
